import React, { useMemo, useState } from 'react'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Button, NoContent, Table } from '../../../../views/components'
import { formatStandard } from 'utils/date'
import { apiStateSelectors } from '../../../api/apiState'
import { agencyApiSelectors } from '../../../api/agency'
import { userApiEvents, userApiSelectors } from '../../../api/user'
import { TAdminAgencyMember } from '../../../api/user/types'
import RemoveMembershipModal, { TRemoveTarget } from '../RemoveMembershipModal/RemoveMembershipModal'
import AgencyCombobox, { TAgencyComboOption } from '../AgencyCombobox/AgencyCombobox'
import { agencyDisplayName, agencyEntityLine, roleLabel } from '../../labels'
import styles from './AgencyMembers.module.scss'

const memberName = (m: TAdminAgencyMember) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || m.emailAddress || m.userId

const AgencyMembers = () => {
  const dispatch = useDispatch()
  const [agency, setAgency] = useState<TAgencyComboOption | null>(null)
  const [removeTarget, setRemoveTarget] = useState<TRemoveTarget | null>(null)

  const agencyListing = useSelector(agencyApiSelectors.getAgencyListing)
  const membersByAgency = useSelector(userApiSelectors.getAdminAgencyMembers)
  const isLoading = useSelector(state => apiStateSelectors.isLoading(state)([userApiEvents.adminAgencyMembers_request]))

  const options = useMemo<TAgencyComboOption[]>(
    () =>
      (agencyListing ?? [])
        .filter(a => a.agencyId)
        .map(a => ({
          agencyId: a.agencyId,
          title: agencyDisplayName(a),
          subtitle: agencyEntityLine(a),
          isTest: !!a.isTestAgency,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [agencyListing],
  )

  // Earliest first, so the agency's original users lead; rows without a date go last.
  const members = useMemo(() => {
    const rows = agency ? membersByAgency[agency.agencyId] : undefined
    if (!rows) return undefined
    const time = (m: TAdminAgencyMember) => (m.createdAt ? new Date(m.createdAt).getTime() : Number.POSITIVE_INFINITY)
    return [...rows].sort((a, b) => time(a) - time(b))
  }, [agency, membersByAgency])
  const unreachable = (members ?? []).filter(m => !m.canLogin).length

  const pick = (option: TAgencyComboOption | null) => {
    setAgency(option)
    if (option) dispatch(userApiEvents.adminAgencyMembers_request({ agencyId: option.agencyId }))
  }

  const confirmRemove = (target: TRemoveTarget) => {
    dispatch(userApiEvents.adminRemoveMembership_request({ userId: target.userId, agencyId: target.agencyId }))
    setRemoveTarget(null)
  }

  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        <h3>Pick an agency</h3>
        <p>Lists every member and whether login can reach them.</p>
      </div>

      <div className={styles.picker}>
        <AgencyCombobox options={options} value={agency} onChange={pick} />
      </div>

      {agency && <span className={styles.id}>Agency id {agency.agencyId}</span>}

      {agency && !isLoading && members && members.length === 0 && (
        <NoContent heading="No members" subHeading="Nobody belongs to this agency." />
      )}

      {agency && members && members.length > 0 && (
        <>
          {unreachable > 0 && (
            <p className={styles.notice} role="note">
              {unreachable === 1 ? '1 member is' : `${unreachable} members are`} unreachable: their email logs into a
              different account, so they can never switch into this agency. Look the email up on the By user tab.
            </p>
          )}
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Cell head>User</Table.Cell>
                <Table.Cell head>Role</Table.Cell>
                <Table.Cell head>Created</Table.Cell>
                <Table.Cell head>Last login</Table.Cell>
                <Table.Cell head>Login</Table.Cell>
                <Table.Cell head>Status</Table.Cell>
                <Table.Cell head>{null}</Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {members.map(member => (
                <Table.Row key={member.userId}>
                  <Table.Cell>
                    <span className={styles.member}>
                      <span className={styles['member-name']}>{memberName(member)}</span>
                      {member.emailAddress && <span className={styles['member-email']}>{member.emailAddress}</span>}
                      <span className={styles.id}>{member.userId}</span>
                    </span>
                  </Table.Cell>
                  <Table.Cell>{roleLabel(member.role)}</Table.Cell>
                  <Table.Cell>{member.createdAt ? formatStandard(member.createdAt) : '—'}</Table.Cell>
                  <Table.Cell>{member.lastLoggedIn ? formatStandard(member.lastLoggedIn) : '—'}</Table.Cell>
                  <Table.Cell>
                    <span className={cx(styles.tag, member.canLogin ? styles['tag-ok'] : styles['tag-bad'])}>
                      {member.canLogin ? 'Reachable' : 'Unreachable'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={cx({ [styles.muted]: !member.enabled })}>
                      {member.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </Table.Cell>
                  <Table.Cell className={styles.actions}>
                    <Button
                      size="xs"
                      secondary
                      onClick={() =>
                        setRemoveTarget({
                          userId: member.userId,
                          agencyId: agency.agencyId,
                          userLabel: memberName(member),
                          agencyLabel: agency.title,
                        })
                      }
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      <RemoveMembershipModal target={removeTarget} onCancel={() => setRemoveTarget(null)} onConfirm={confirmRemove} />
    </div>
  )
}

export default AgencyMembers
